import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import StatusIndicator from './StatusIndicator';
import { useBotLink } from '../hooks/useBotLink';
import { usePricingData } from '../hooks/usePricingData';
import { useTelegramModal } from '../context/TelegramModalContext';
import { RocketIcon } from './Icons';
import {
  buildPricingCards,
  buildTariffKey,
  formatDevices,
  formatTraffic,
} from '../utils/pricing';
import { PROXY_SERVERS } from '../config/proxy';

function Pricing() {
  const { tariffs, error, isLoading } = usePricingData();
  const botLink = useBotLink();
  const { openTelegramModal } = useTelegramModal();
  const trialLink = botLink ? `${botLink}?start=trial` : '';

  const { paidTariffs, pricingMeta } = useMemo(() => {
    if (!tariffs.length) return { paidTariffs: [], pricingMeta: null };
    const meta = buildPricingCards(tariffs);
    const paid = meta.orderedTariffs.filter(t => t.price !== 0);
    return { paidTariffs: paid, pricingMeta: meta };
  }, [tariffs]);

  const renderPaidCards = () => {
    if (!pricingMeta) return null;
    const { cheapestKey, popularKey, bestKey, maxPricePerDay } = pricingMeta;

    return paidTariffs.map((tariff) => {
      const tariffKey = buildTariffKey(tariff);
      const pricePerDay = tariff.price / tariff.duration_days;
      let economy = 0;
      let badge = null;
      const isPopular = tariffKey === popularKey && paidTariffs.length > 1;
      const isBest = tariffKey === bestKey && paidTariffs.length > 1;
      const isCheapest = tariffKey === cheapestKey && !isPopular && !isBest && paidTariffs.length > 1;
      const actionLink = botLink || '';

      if (maxPricePerDay > 0) {
        economy = Math.round((1 - (pricePerDay / maxPricePerDay)) * 100);
      }
      if (isBest && economy >= 5) badge = `−${economy}%`;

      let variant = '';
      let labelText = '';
      if (isPopular) { variant = 'popular'; labelText = 'Популярный'; }
      else if (isBest) { variant = 'best'; labelText = 'Лучшая цена'; }
      else if (isCheapest) { variant = 'cheapest'; }

      return (
        <div key={tariffKey} className={`pc ${variant ? `pc--${variant}` : ''}`}>
          {labelText && <div className={`pc__label pc__label--${variant}`}>{labelText}</div>}
          {badge && <div className="pc__badge">{badge}</div>}

          <div className="pc__head">
            <span className="pc__duration">{tariff.label.replace(/месяц(а|ев)?/gi, 'мес.')}</span>
          </div>

          <div className="pc__price-row">
            <span className="pc__amount">{tariff.price}</span>
            <span className="pc__currency">₽</span>
          </div>

          <ul className="pc__features">
            <li className="pc__feature">
              <svg className="pc__feature-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
              </svg>
              <span>{tariff.traffic_gb === 0 ? 'Безлимитный трафик' : `${formatTraffic(tariff.traffic_gb)} трафика`}</span>
            </li>
            <li className="pc__feature">
              <svg className="pc__feature-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12" y2="18" />
              </svg>
              <span>{formatDevices(tariff.device_limit)} {tariff.device_limit === 1 ? 'устройство' : tariff.device_limit > 1 && tariff.device_limit < 5 ? 'устройства' : 'устройств'}</span>
            </li>
          </ul>

          <a
            href={actionLink || undefined}
            className="pc__btn"
            onClick={(e) => {
              e.preventDefault();
              if (actionLink) openTelegramModal(actionLink);
            }}
            style={!actionLink ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
          >
            Подключить
          </a>
        </div>
      );
    });
  };

  return (
    <section className="pricing" id="pricing">
      <div className="pricing__container">
        <h2 className="pricing__title">Тарифы</h2>
        <p className="pricing__subtitle">Выберите оптимальный план подключения</p>

        {isLoading && <StatusIndicator variant="loading" className="pricing__loading" />}
        {error && <StatusIndicator variant="error" className="pricing__error" />}

        {!isLoading && !error && paidTariffs.length > 0 && (
          <div className="pricing__grid" id="pricingGrid">
            {renderPaidCards()}
          </div>
        )}

        {/* Trial */}
        <div className="pricing__bottom-row">
          <a
            href={trialLink || undefined}
            className="trial-card"
            onClick={(e) => {
              e.preventDefault();
              if (trialLink) openTelegramModal(trialLink);
            }}
            style={!trialLink ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
          >
            <div className="trial-card__content">
              <span className="trial-card__tag">Бесплатно</span>
              <span className="trial-card__title">Попробуйте подписку</span>
              <span className="trial-card__desc">Оцените скорость и качество сервиса — без оплаты</span>
            </div>
            <span className="trial-card__btn">
              <RocketIcon className="trial-card__btn-icon" />
              Активировать
            </span>
          </a>

          {PROXY_SERVERS.length > 0 ? (
            <Link to="/proxy" className="proxy-row">
              <div className="proxy-row__badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              </div>
              <span className="proxy-row__text"><strong>Telegram не работает?</strong> Подключите бесплатный прокси</span>
              <svg className="proxy-row__arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>
            </Link>
          ) : (
            <div className="proxy-row proxy-row--disabled" style={{ opacity: 0.6, cursor: 'default' }}>
              <div className="proxy-row__badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
              </div>
              <span className="proxy-row__text">Нет доступных прокси (технические работы)</span>
            </div>
          )}
        </div>

      </div>
    </section>
  );
}

export default Pricing;
